
import { supabase } from '../lib/supabaseClient';
import { CONFIG } from '../lib/config';
import { UserProfile, UserStats, CurriculumData, LessonContent, Language } from '../types';

const DEFAULT_STATS: UserStats = { 
  math: 0, chinese: 0, english: 0, science: 0, history: 0, art: 0, 
  focus_minutes: 0, tasks_completed: 0 
};

const STORAGE_KEYS = {
  PROFILE: 'wv_profile',
  CURRICULUM: 'wv_curriculum',
  LESSON_CACHE: 'wv_lesson_cache',
  TTS_CACHE: 'wv_tts_cache'
};

// Robust hash function for text keys
const getTextHash = (text: string): string => {
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash = ((hash << 5) - hash) + text.charCodeAt(i);
    hash |= 0;
  }
  return `v2_${Math.abs(hash)}_${text.length}`;
};

export const userService = {
  async signUp(email: string, password: string, name: string) {
    if (!CONFIG.IS_SUPABASE_CONFIGURED) throw new Error("Local-only mode: Signing up is disabled.");
    return await supabase.auth.signUp({
      email, password, options: { data: { full_name: name } }
    });
  },

  async signIn(email: string, password: string) {
    if (!CONFIG.IS_SUPABASE_CONFIGURED) {
       // Mock sign-in for local mode
       localStorage.setItem('wv_local_auth', 'true');
       return { data: { user: { id: 'local-user' } }, error: null };
    }
    return await supabase.auth.signInWithPassword({ email, password });
  },

  async signOut() {
    localStorage.removeItem('wv_local_auth');
    if (CONFIG.IS_SUPABASE_CONFIGURED) await supabase.auth.signOut();
  },

  async getSession() {
    if (!CONFIG.IS_SUPABASE_CONFIGURED) {
       const isAuth = localStorage.getItem('wv_local_auth');
       return { data: { session: isAuth ? { user: { id: 'local-user' } } : null } };
    }
    return await supabase.auth.getSession();
  },

  async getProfile(): Promise<UserProfile | null> {
    if (!CONFIG.IS_SUPABASE_CONFIGURED) {
      const local = localStorage.getItem(STORAGE_KEYS.PROFILE);
      if (local) return JSON.parse(local);
      return {
        uid: 'local-user',
        name: 'Explorer',
        grade: 1,
        age: 7,
        interests: [],
        avatar: 'rocket',
        level: 1,
        xp: 0,
        energy: 100,
        maxEnergy: 100,
        streak: 0,
        skills: [],
        stats: DEFAULT_STATS
      };
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (error) return null;

    return {
      uid: data.id,
      name: data.full_name || 'Explorer',
      grade: data.grade || 1,
      age: data.age || 7,
      interests: data.interests || [],
      avatar: data.avatar || 'rocket',
      level: data.level || 1,
      xp: data.xp || 0,
      energy: data.energy ?? 100,
      maxEnergy: data.max_energy || 100,
      streak: data.streak || 0,
      skills: data.unlocked_skills || [],
      stats: { ...DEFAULT_STATS, ...data.stats }
    };
  },

  async getCurriculum(): Promise<CurriculumData | null> {
    if (!CONFIG.IS_SUPABASE_CONFIGURED) {
      const local = localStorage.getItem(STORAGE_KEYS.CURRICULUM);
      return local ? JSON.parse(local) : null;
    }
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_curriculums')
      .select('data')
      .eq('user_id', user.id)
      .single();
    return data ? data.data : null;
  },

  async saveCurriculum(curriculum: CurriculumData) {
    localStorage.setItem(STORAGE_KEYS.CURRICULUM, JSON.stringify(curriculum));
    if (!CONFIG.IS_SUPABASE_CONFIGURED) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    await supabase.from('user_curriculums').upsert({
      user_id: user.id,
      data: curriculum,
      updated_at: new Date().toISOString()
    });
  },

  async updateProfile(updates: Partial<UserProfile>) {
    const current = await this.getProfile();
    if (current) {
      localStorage.setItem(STORAGE_KEYS.PROFILE, JSON.stringify({ ...current, ...updates }));
    }
    
    if (!CONFIG.IS_SUPABASE_CONFIGURED) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const dbUpdates: any = {};
    if (updates.xp !== undefined) dbUpdates.xp = updates.xp;
    if (updates.level !== undefined) dbUpdates.level = updates.level;
    if (updates.energy !== undefined) dbUpdates.energy = updates.energy;
    if (updates.skills !== undefined) dbUpdates.unlocked_skills = updates.skills;
    if (updates.stats !== undefined) dbUpdates.stats = updates.stats;
    if (updates.name !== undefined) dbUpdates.full_name = updates.name;
    if (updates.grade !== undefined) dbUpdates.grade = updates.grade;
    if (updates.age !== undefined) dbUpdates.age = updates.age;
    if (updates.interests !== undefined) dbUpdates.interests = updates.interests;
    if (updates.avatar !== undefined) dbUpdates.avatar = updates.avatar;

    dbUpdates.updated_at = new Date().toISOString();
    await supabase.from('profiles').update(dbUpdates).eq('id', user.id);
  },

  async logLearning(action: string, subject: string, xp: number, details: any = {}) {
    if (!CONFIG.IS_SUPABASE_CONFIGURED) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    supabase.from('learning_logs').insert({
      user_id: user.id, action_type: action, subject, xp_gained: xp, details
    });
  },

  async getGlobalLessonContent(nodeId: string, grade: number, lang: Language): Promise<LessonContent | null> {
    const cacheKey = `${nodeId}_${grade}_${lang}`;
    const local = localStorage.getItem(STORAGE_KEYS.LESSON_CACHE);
    const cache = local ? JSON.parse(local) : {};
    if (cache[cacheKey]) return cache[cacheKey];

    if (!CONFIG.IS_SUPABASE_CONFIGURED) return null;

    try {
      const { data, error } = await supabase
        .from('course_cache')
        .select('content')
        .eq('node_id', nodeId)
        .eq('grade', grade)
        .eq('lang', lang)
        .maybeSingle();

      return data?.content || null;
    } catch (e) {
      return null;
    }
  },

  async saveGlobalLessonContent(nodeId: string, grade: number, lang: Language, content: LessonContent) {
    const cacheKey = `${nodeId}_${grade}_${lang}`;
    const local = localStorage.getItem(STORAGE_KEYS.LESSON_CACHE);
    const cache = local ? JSON.parse(local) : {};
    cache[cacheKey] = content;
    localStorage.setItem(STORAGE_KEYS.LESSON_CACHE, JSON.stringify(cache));

    if (!CONFIG.IS_SUPABASE_CONFIGURED) return;
    await supabase.from('course_cache').upsert({
      node_id: nodeId, grade, lang, content, created_at: new Date().toISOString()
    });
  },

  async getTTSCache(text: string): Promise<string | null> {
    const hash = getTextHash(text);
    const local = localStorage.getItem(STORAGE_KEYS.TTS_CACHE);
    const cache = local ? JSON.parse(local) : {};
    if (cache[hash]) return cache[hash];

    if (!CONFIG.IS_SUPABASE_CONFIGURED) return null;
    const { data } = await supabase.from('tts_cache').select('audio_base64').eq('text_hash', hash).maybeSingle();
    return data?.audio_base64 || null;
  },

  async saveTTSCache(text: string, base64: string) {
    const hash = getTextHash(text);
    const local = localStorage.getItem(STORAGE_KEYS.TTS_CACHE);
    const cache = local ? JSON.parse(local) : {};
    cache[hash] = base64;
    localStorage.setItem(STORAGE_KEYS.TTS_CACHE, JSON.stringify(cache));

    if (!CONFIG.IS_SUPABASE_CONFIGURED) return;
    await supabase.from('tts_cache').upsert({
      text_hash: hash, audio_base64: base64, original_text: text.slice(0, 500)
    });
  }
};
